import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import AIComparableReport from '@/components/TaxReport/AIComparables';
import ComparableReport from '@/components/TaxReport/ComparableReport';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import useGetProfile from '@/hooks/useGetProfile';
import useGetReport from '@/hooks/useGetReport';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import { set } from 'lodash';
import { Download } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const PropertyTaxReport = () => {
  const { id } = useParams();
  const { data: profile } = useGetProfile();
  const { data: report } = useGetReport(id!);
  const reportRef = useRef<HTMLDivElement>(null);

  const [marketFairValue, setMarketFairValue] = useState('');
  const [isCapture, setIsCapture] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');

  useEffect(() => {
    if (profile) {
      setOwnerName(`${profile.firstName} ${profile.lastName}`);
      setOwnerEmail(profile.emailAddress || '');
      setOwnerPhone(profile.phoneNumber || '');
    }
  }, [profile]);

  useEffect(() => {
    if (report) {
      setMarketFairValue(report.staticAppeal.opinionOfValue);
    }
  }, [report]);

  const handleSavePDF = async () => {
    if (!reportRef.current) return;
    setIsCapture(true);

    const opt = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: 'property-tax-report.pdf',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    html2pdf().set(opt).from(reportRef.current).save();

    setTimeout(() => setIsCapture(false), 1000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-7xl">
        {/* Save to PDF Button */}
        <div className="flex justify-end mb-4">
          <Button onClick={handleSavePDF} className="gap-2">
            <Download className="h-4 w-4" />
            Save to PDF
          </Button>
        </div>

        <div ref={reportRef}>
          <div className={cn(isCapture ? 'mb-[400px]' : 'mb-12')}>
            <h2 className="text-3xl md:text-3xl lg:text-5xl font-bold text-primary mb-4 text-center">
              Instructions to Property Owner
            </h2>
            <ul className="list-disc pl-12">
              <li>
                <span className="font-semibold">Disclaimer: </span>
                <p>
                  You must verify all protest deadlines, procedures, and
                  required documentation with the{' '}
                  {report?.staticAppeal?.countyName}. Local rules and
                  requirements may change, and it is your responsibility to
                  ensure compliance.
                </p>
              </li>
              <li>
                <span className="font-semibold">Review and Edit: </span>
                <p>
                  Carefully review and edit this draft before submission. Attach
                  any supporting documents (such as photos, appraisals, or
                  additional comparable sales) as required by{' '}
                  {report?.staticAppeal?.assessorOfficeName}.
                </p>
              </li>
              <li>
                <span className="font-semibold">Legal Reference: </span>
                <p>
                  For more information, see{' '}
                  <a
                    href={report?.staticInstructions?.websiteUrl}
                    target="_blank"
                    className="underline text-blue-600"
                  >
                    {report?.staticInstructions?.websiteUrl}
                  </a>
                </p>
              </li>
              <li>
                <p className="font-semibold">Contact Information: </p>
                <p>Name: {report?.staticInstructions?.countyName}</p>
                <p>
                  Address:{' '}
                  {report?.staticInstructions?.addressLines?.join(', ')}
                </p>
                <p>Phone: {report?.staticInstructions?.phone}</p>
              </li>
              <li>
                <span className="font-semibold">How to File: </span>
                <p>
                  You may file your protest online through the{' '}
                  <a href={report?.staticInstructions?.portalUrl}>
                    {report?.staticInstructions?.countyName}'s' Portal
                  </a>
                  , by mail to the address above, or in person at the Assessor’s
                  Office. Check the website or call for current instructions and
                  deadlines.
                </p>
              </li>
              <li>
                <span className="font-semibold">Professional Advice: </span>
                <p>
                  If you have questions about your property’s value or the
                  protest process, consider consulting your real estate agent or
                  a qualified property appraiser.
                </p>
              </li>
            </ul>
          </div>
          {isCapture && (
            <div className="mb-[180px]">
              <h2 className="text-3xl md:text-3xl lg:text-5xl font-bold text-primary mb-4 text-center">
                Appeal of Property Valuation
              </h2>
              <div className="space-y-3 mb-8 mt-5">
                <p>
                  Owner's Name:{' '}
                  <span className="font-semibold">{ownerName}</span>
                </p>
                <p>
                  Mailing Address:{' '}
                  <span className="font-semibold">
                    {report?.staticAppeal?.mailingAddress}
                  </span>
                </p>
                <p>
                  Phone Number:{' '}
                  <span className="font-semibold">{ownerPhone}</span>
                </p>
                <p>
                  Email: <span className="font-semibold">{ownerEmail}</span>
                </p>
                <p>
                  Date:{' '}
                  <span className="font-semibold">
                    {dayjs().format('MMMM DD, YYYY')}
                  </span>
                </p>
              </div>
              <div>
                <p>{report?.staticInstructions?.countyName}</p>
                <p>{report?.staticInstructions?.addressLines?.join(', ')}</p>
                <p>Re: Subject Property Address: {report?.address}</p>
              </div>
              <div className="mt-8 space-y-3">
                <p>
                  APN (Assessor Parcel Number):{' '}
                  <span className="font-semibold">
                    {report?.staticAppeal?.apn}
                  </span>
                </p>
                <p>
                  Legal Description:{' '}
                  <span className="font-semibold">
                    {report?.staticAppeal?.legalDescription}
                  </span>
                </p>
              </div>
              <div className="mt-1 space-y-8">
                <p>Dear {report?.staticAppeal?.countyName}</p>
                <p>
                  I am writing to formally appeal the assessed value of my
                  property listed above. I believe the current valuation does
                  not accurately reflect the true market value of my home as of
                  the relevant valuation date. The Assessor’s Office may not be
                  aware of the property’s specific condition or unique
                  circumstances that affect its value. To support my appeal, I
                  have reviewed multiple recent comparable sales in my
                  neighborhood and made market-based adjustments for differences
                  in condition, features, garage/outbuildings,
                  lot/view/location, living area, and sale date. Based on this
                  analysis, I respectfully request that the assessed value be
                  reduced to reflect a fair and accurate market value. My
                  opinion of the fair market value, supported by the comparable
                  sales analysis below, is{' '}
                  <span className="font-bold">{marketFairValue}</span>.
                </p>
                <p>Sincerely</p>
                <p className="font-bold">{ownerName}</p>
              </div>
            </div>
          )}
          {/* Header Section */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-3">
              Property Tax Valuation Appeal
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive valuation analysis and comparable sales review
            </p>
          </div>

          {/* Property Information Card */}
          <Card className="mb-8 shadow-lg">
            <CardHeader className="bg-secondary/30">
              <CardTitle className="text-2xl text-primary">
                Subject Property Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Owner & Contact Information */}
                <div>
                  <h3 className="text-sm font-semibold text-primary mb-3">
                    Owner & Contact Information
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Owner Name
                      </p>
                      {isCapture ? (
                        <p
                          className={cn(
                            'font-semibold text-foreground focus:outline-none w-full',
                          )}
                        >
                          {ownerName}
                        </p>
                      ) : (
                        <input
                          value={ownerName}
                          className={cn(
                            'font-semibold text-foreground focus:outline-none w-full',
                          )}
                          onChange={(e) => setOwnerName(e.target.value)}
                        />
                      )}
                    </div>
                    <div>
                      <p
                        className={cn(
                          'text-sm font-medium text-muted-foreground',
                        )}
                      >
                        Email
                      </p>
                      {isCapture ? (
                        <p className="text-base text-foreground focus:outline-none w-full">
                          {ownerEmail}
                        </p>
                      ) : (
                        <input
                          value={ownerEmail}
                          className="text-base text-foreground focus:outline-none w-full"
                          onChange={(e) => setOwnerEmail(e.target.value)}
                        />
                      )}
                    </div>
                    <div>
                      <p
                        className={cn(
                          'text-sm font-medium text-muted-foreground',
                        )}
                      >
                        Phone
                      </p>
                      {isCapture ? (
                        <p className="text-base text-foreground focus:outline-none w-full">
                          {ownerPhone}
                        </p>
                      ) : (
                        <input
                          value={ownerPhone}
                          className="text-base text-foreground focus:outline-none w-full"
                          onChange={(e) => setOwnerPhone(e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Property Address & Legal */}
                <div>
                  <h3 className="text-sm font-semibold text-primary mb-3">
                    Property Address & Legal
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Property Address
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.address}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        APN (Assessor Parcel Number)
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.staticAppeal?.apn}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Legal Description
                      </p>
                      <p className="text-base text-foreground">
                        {report?.staticAppeal.legalDescription}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Property Details */}
                <div>
                  <h3 className="text-sm font-semibold text-primary mb-3">
                    Property Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Lot Size
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo.lotSquareFeet}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Living Area Sqft (above grade)
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo.livingSquareFeet} sq ft
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Basement Sqft (total)
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo.basementSquareFeet}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Finished Basement Sqft
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo?.basementSquareFeetFinished}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Beds
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo?.bedrooms}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Baths
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo?.bathrooms}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Garage Size Sqft
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo?.garageSquareFeet}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Year Built
                      </p>
                      <p className="text-base font-semibold text-foreground">
                        {report?.propertyInfo?.yearBuilt}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subject Property Photos */}
          {!!report?.images?.length && (
            <Card className="mb-8 shadow-lg">
              <CardHeader className="bg-secondary/30">
                <CardTitle className="text-2xl text-primary">
                  Subject Property Photos
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <div
                    className={cn(
                      'pb-4',
                      isCapture ? 'grid grid-cols-4 gap-2' : 'flex gap-4',
                    )}
                    style={{ minWidth: 'max-content' }}
                  >
                    {report?.images?.map((photo, index) => (
                      <div
                        key={index}
                        className="w-40 h-28 overflow-hidden rounded-lg border border-border shadow-sm"
                      >
                        <img
                          src={photo}
                          alt=""
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          crossOrigin="anonymous"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fair Market Value Summary */}
          <Card className="mb-8 shadow-lg border-2 border-primary/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Estimated Fair Market Value
                </p>
                {isCapture ? (
                  <p
                    className={cn(
                      'text-4xl md:text-5xl font-bold',
                      'text-center text-primary mt-1',
                    )}
                  >
                    {marketFairValue}
                  </p>
                ) : (
                  <input
                    type="text"
                    className={cn(
                      'text-4xl md:text-5xl font-bold text-primary',
                      'focus:outline-none',
                      'text-center',
                    )}
                    value={marketFairValue}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(
                        /[^0-9.]/g,
                        '',
                      );

                      if (!numericValue) {
                        setMarketFairValue('');
                        return;
                      }

                      const formatted = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }).format(Number(numericValue));

                      setMarketFairValue(formatted);
                    }}
                    placeholder="$0.00"
                  />
                )}
                <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto">
                  Based on comprehensive analysis of comparable sales with
                  appropriate adjustments for size, features, location,
                  condition, and date of sales
                </p>
              </div>
            </CardContent>
          </Card>

          {report?.aiComparables?.length > 0 && (
            <div className="mb-12">
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                  AI-Enhanced Comparable Sales Analysis
                </h2>
                <p className="text-muted-foreground">
                  Advanced analysis with AI photo analysis comparing subject to
                  comparable sales with market based adjustments.
                </p>
              </div>

              <div className="space-y-6">
                {report?.aiComparables.map((comp, index) => (
                  <AIComparableReport
                    comparable={comp}
                    report={report}
                    key={index}
                    isCapture={isCapture}
                  />
                ))}
              </div>
            </div>
          )}

          {report?.comparables.length > 0 && (
            <div className="mb-12">
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                  Comparable Sales Analysis
                </h2>
                <p className="text-muted-foreground">
                  comparable sales with market based adjustments.
                </p>
              </div>

              <div className="space-y-6">
                {report?.comparables.map((comp, index) => (
                  <ComparableReport
                    comparable={comp}
                    report={report}
                    key={index}
                    isCapture={isCapture}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Assessor Contact Information */}
          <Card className="shadow-lg bg-secondary/20">
            <CardHeader>
              <CardTitle className="text-xl text-primary">
                Assessor Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-muted-foreground">
                    Office:{' '}
                  </span>
                  <span className="font-semibold text-foreground">
                    {report?.staticInstructions?.countyName}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">
                    Address:{' '}
                  </span>
                  <span className="text-foreground">
                    {report?.staticInstructions.addressLines.join(' ')}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Phone: </span>
                  <span className="text-foreground">
                    {report?.staticInstructions.phone}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};
export default PropertyTaxReport;
